<template>
  <div class="admin-view">
    <div class="page-header">
      <h1>Admin Dashboard</h1>
      <p>System overview and administration</p>
    </div>

    <StatsGrid :stats="stats" />

    <div class="content-grid">
      <ActivityList :recent-requests="recentRequests" :loading="loading" />
      <QuickActions @refresh="refreshData" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { api } from '@/composables/api'
import StatsGrid from '@/components/dashboard/StatsGrid.vue'
import ActivityList from '@/components/dashboard/ActivityList.vue'
import QuickActions from '@/components/dashboard/QuickActions.vue'

const loading = ref(true)
const stats = ref({
  totalRequests: 0,
  pendingRequests: 0,
  deliveredEquipment: 0,
  totalEquipment: 0
})

const recentRequests = ref<any[]>([])

const loadStats = async () => {
  try {
    // Load request stats
    const reqStats = await api.get('/requests/stats')
    if (reqStats.data) {
      stats.value.totalRequests = reqStats.data.total || 0
      stats.value.pendingRequests = reqStats.data.by_status?.pending || 0
    }

    // Load equipment stats
    const eqStats = await api.get('/equipment/stats')
    if (eqStats.data) {
      const available = eqStats.data.available || 0
      const processing = eqStats.data.processing || 0
      const troubleshooting = eqStats.data.troubleshooting || 0
      stats.value.totalEquipment = available + processing + troubleshooting
      stats.value.deliveredEquipment = eqStats.data.delivered || 0
    }
  } catch (error) {
    console.error('Error loading stats:', error)
  }
}

const loadRecentRequests = async () => {
  try {
    const response = await api.get('/requests?limit=5')
    if (response.data) {
      recentRequests.value = response.data.slice(0, 5)
    }
  } catch (error) {
    console.error('Error loading recent requests:', error)
  }
}

const refreshData = async () => {
  loading.value = true
  await Promise.all([loadStats(), loadRecentRequests()])
  loading.value = false
}

onMounted(async () => {
  await refreshData()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.admin-view {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.page-header p {
  color: var(--text-light, #666666);
  margin: 0;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 1.5rem;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
